import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { apiFetch, API_BASE } from "../../utils/api";
import { Search, Loader2, Plus, Image as ImageIcon, Upload as UploadIcon, HardDrive, X } from "lucide-react";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk (sotto il limite proxy)

function xhrPostForm(url, formData, { onProgress, token } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
      } else {
        let detail = xhr.responseText;
        try { detail = JSON.parse(xhr.responseText).detail || detail; } catch { /* ignore */ }
        reject(new Error(detail || `HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Errore di rete durante upload"));
    xhr.ontimeout = () => reject(new Error("Timeout upload"));
    xhr.send(formData);
  });
}

export function MediaPicker({ selected, onChange }) {
  const [tab, setTab] = useState("pc");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { fileName, percent, index, total }
  const [library, setLibrary] = useState([]);
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveQuery, setDriveQuery] = useState("");
  const [driveLoading, setDriveLoading] = useState(false);

  const refreshLibrary = async () => {
    const r = await apiFetch("/media/library");
    if (r.ok) setLibrary(await r.json());
  };

  useEffect(() => { refreshLibrary(); }, []);

  const uploadFileChunked = async (file, idx, total) => {
    const token = localStorage.getItem("session_token");
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

    // 1) init
    const initRes = await apiFetch("/media/upload-init", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        total_size: file.size,
        total_chunks: totalChunks,
      }),
    });
    if (!initRes.ok) {
      const err = await initRes.json().catch(() => ({}));
      throw new Error(err.detail || "Errore init upload");
    }
    const { upload_id } = await initRes.json();

    // 2) chunks (sequenziali per non saturare il device)
    let uploadedBytes = 0;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append("upload_id", upload_id);
      fd.append("chunk_index", String(i));
      fd.append("chunk", blob, `chunk_${i}`);

      const baseUploaded = uploadedBytes;
      await xhrPostForm(`${API_BASE}/media/upload-chunk`, fd, {
        token,
        onProgress: (loaded) => {
          const currentTotal = baseUploaded + loaded;
          const percent = Math.min(99, Math.round((currentTotal / file.size) * 100));
          setUploadProgress({ fileName: file.name, percent, index: idx, total });
        },
      });
      uploadedBytes = end;
      setUploadProgress({
        fileName: file.name,
        percent: Math.min(99, Math.round((uploadedBytes / file.size) * 100)),
        index: idx,
        total,
      });
    }

    // 3) complete
    const completeRes = await apiFetch("/media/upload-complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ upload_id }),
    });
    if (!completeRes.ok) {
      const err = await completeRes.json().catch(() => ({}));
      throw new Error(err.detail || "Errore finalizzazione upload");
    }
    setUploadProgress({ fileName: file.name, percent: 100, index: idx, total });
    return await completeRes.json();
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadProgress({ fileName: f.name, percent: 0, index: i + 1, total: files.length });
        try {
          const m = await uploadFileChunked(f, i + 1, files.length);
          onChange([...selected, { file_id: m.file_id, url: m.url, name: m.original_filename, mime: m.content_type }]);
        } catch (err) {
          console.error("Upload error", err);
          toast.error(`Upload fallito ${f.name}: ${err.message || err}`);
        }
      }
      await refreshLibrary();
    } finally {
      setUploading(false);
      setUploadProgress(null);
      e.target.value = "";
    }
  };

  const pickFromLibrary = (m) => {
    if (selected.find(s => s.file_id === m.file_id)) return;
    const baseUrl = (window.location.origin + "/api/public-media/" + m.file_id);
    onChange([...selected, { file_id: m.file_id, url: baseUrl, name: m.original_filename, mime: m.content_type }]);
  };

  const remove = (file_id) => onChange(selected.filter(s => s.file_id !== file_id));

  const loadDriveStatus = async () => {
    const r = await apiFetch("/drive/status");
    if (r.ok) setDriveStatus(await r.json());
  };
  const loadDriveFiles = async (q = "") => {
    setDriveLoading(true);
    try {
      const url = q ? `/drive/files?q=${encodeURIComponent(q)}` : "/drive/files";
      const r = await apiFetch(url);
      if (r.ok) {
        const data = await r.json();
        setDriveFiles(data.files || []);
      } else if (r.status === 400) {
        setDriveStatus({ connected: false });
      }
    } finally { setDriveLoading(false); }
  };
  const connectDrive = async () => {
    const r = await apiFetch("/drive/connect");
    if (r.ok) { const d = await r.json(); window.location.href = d.auth_url; }
  };
  const importDriveFile = async (driveFileId) => {
    setDriveLoading(true);
    try {
      const r = await apiFetch("/drive/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_id: driveFileId }),
      });
      if (r.ok) {
        const m = await r.json();
        onChange([...selected, { file_id: m.file_id, url: m.url, name: m.original_filename, mime: m.content_type }]);
        await refreshLibrary();
        toast.success("Importato da Drive");
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore import da Drive");
      }
    } finally { setDriveLoading(false); }
  };

  useEffect(() => {
    if (tab === "drive") {
      loadDriveStatus();
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "drive" && driveStatus?.connected) {
      loadDriveFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driveStatus?.connected, tab]);

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium block">Media (immagini / video)</label>
      {selected.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {selected.map(m => (
            <div key={m.file_id} className="relative group" data-testid={`media-selected-${m.file_id}`}>
              {m.mime?.startsWith("video/") ? (
                <div className="w-16 h-16 rounded-md bg-slate-200 flex items-center justify-center text-[10px] text-slate-600">VIDEO</div>
              ) : (
                <img src={m.url} alt="" className="w-16 h-16 rounded-md object-cover" />
              )}
              <button onClick={() => remove(m.file_id)} type="button" className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center" data-testid={`media-remove-${m.file_id}`}><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-2">
        <TabsList className="bg-slate-100 p-0.5 h-auto">
          <TabsTrigger value="pc" className="text-xs h-7 data-[state=active]:bg-white" data-testid="media-tab-pc"><UploadIcon className="w-3 h-3 mr-1" />PC</TabsTrigger>
          <TabsTrigger value="library" className="text-xs h-7 data-[state=active]:bg-white" data-testid="media-tab-library"><ImageIcon className="w-3 h-3 mr-1" />Libreria</TabsTrigger>
          <TabsTrigger value="drive" className="text-xs h-7 data-[state=active]:bg-white" data-testid="media-tab-drive"><HardDrive className="w-3 h-3 mr-1" />Drive</TabsTrigger>
        </TabsList>
        <TabsContent value="pc" className="m-0">
          <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-slate-50 transition-colors ${uploading ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="media-upload-zone">
            <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} data-testid="media-file-input" />
            {uploading ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : <UploadIcon className="w-5 h-5 text-slate-400" />}
            <div className="text-xs text-slate-600 mt-1">Trascina qui o clicca per caricare</div>
            <div className="text-[10px] text-slate-400 mt-0.5">jpg, png, mp4, mov · upload a chunk (nessun limite di dimensione)</div>
            {uploadProgress && (
              <div className="w-full mt-3" data-testid="media-upload-progress">
                <div className="flex items-center justify-between text-[10px] text-slate-600 mb-1">
                  <span className="truncate max-w-[70%]" title={uploadProgress.fileName}>
                    {uploadProgress.total > 1 ? `(${uploadProgress.index}/${uploadProgress.total}) ` : ""}{uploadProgress.fileName}
                  </span>
                  <span className="tabular-nums">{uploadProgress.percent}%</span>
                </div>
                <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all duration-200" style={{ width: `${uploadProgress.percent}%` }} />
                </div>
              </div>
            )}
          </label>
        </TabsContent>
        <TabsContent value="library" className="m-0">
          <div className="max-h-44 overflow-y-auto border rounded-md p-2" data-testid="media-library-list">
            {library.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-4">Nessun media caricato ancora.</div>
            ) : (
              <div className="grid grid-cols-5 gap-1.5">
                {library.map(m => (
                  <button key={m.file_id} type="button" onClick={() => pickFromLibrary(m)} className="relative group" data-testid={`media-library-${m.file_id}`}>
                    {m.content_type?.startsWith("video/") ? (
                      <div className="w-full aspect-square rounded bg-slate-200 flex items-center justify-center text-[10px] text-slate-600">VIDEO</div>
                    ) : (
                      <img src={window.location.origin + "/api/public-media/" + m.file_id} alt="" className="w-full aspect-square object-cover rounded" />
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded">
                      <Plus className="w-4 h-4 text-white" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="drive" className="m-0">
          {!driveStatus?.connected ? (
            <div className="border rounded-md p-3 text-center bg-blue-50">
              <HardDrive className="w-5 h-5 text-blue-600 mx-auto" />
              <div className="text-xs text-slate-700 mt-1">Collega il tuo Google Drive per accedere ai tuoi file</div>
              <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700 text-white" onClick={connectDrive} data-testid="drive-connect-btn">Collega Google Drive</Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-1">
                <Input placeholder="Cerca file Drive..." value={driveQuery} onChange={(e) => setDriveQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); loadDriveFiles(driveQuery); } }} className="h-7 text-xs" data-testid="drive-search-input" />
                <Button size="sm" variant="outline" onClick={() => loadDriveFiles(driveQuery)} data-testid="drive-search-btn"><Search className="w-3 h-3" /></Button>
              </div>
              <div className="max-h-44 overflow-y-auto border rounded-md p-2" data-testid="drive-files-list">
                {driveLoading ? (
                  <div className="text-xs text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
                ) : driveFiles.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-4">Nessun file trovato.</div>
                ) : (
                  <div className="grid grid-cols-5 gap-1.5">
                    {driveFiles.map(f => (
                      <button key={f.id} type="button" onClick={() => importDriveFile(f.id)} className="relative group" title={f.name} data-testid={`drive-file-${f.id}`}>
                        {f.thumbnailLink ? (
                          <img src={f.thumbnailLink} referrerPolicy="no-referrer" alt="" className="w-full aspect-square object-cover rounded" />
                        ) : (
                          <div className="w-full aspect-square rounded bg-slate-200 flex items-center justify-center text-[9px] text-slate-600 break-all p-1">{f.mimeType?.startsWith("video/") ? "VIDEO" : "FILE"}</div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded">
                          <Plus className="w-4 h-4 text-white" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
